import i18next from 'i18next';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { UiButton } from 'src/components/UI/Button';
import { UiCheckbox } from 'src/components/UI/checkbox/Checkbox';
import { UiDatePicker } from 'src/components/UI/DatePicker';
import { UiFormatNumberInput } from 'src/components/UI/inputs/FormatNumber';
import { UiInput } from 'src/components/UI/inputs/Input';
import { UiTableFilterType, uiTableGetType } from 'src/components/UI/table/UiTable.helpers';
import { FormPropsType } from 'src/components/UI/types';
import { BaseTableFilterFiltersType } from 'src/models/Table';
import { useTHelper } from 'src/utils/i18n';
import { SafeAnyType } from 'src/utils/safeAny';
import { StateFormReturnType } from 'src/utils/stateForm';
import { UiMaskedInput } from '../inputs/MaskedInput';

/** helpers */

const getInputMinName = (name: string) => `${name}_min`;
const getInputMaxName = (name: string) => `${name}_max`;

const getFilter = ({
  name,
  filter,
  formProps,
  t,
}: {
  name: string;
  filter: UiTableFilterType;
  formProps: FormPropsType;
  t: (s: string) => string;
}) => {
  switch (filter.type) {
    case 'integer':
      return (
        <UiFormatNumberInput
          material
          formProps={formProps}
          name={name}
          thousandSeparator=""
          max={999_999_999}
          placeholder={t('placeholders.integer')}
          required
          autoFocus
          allowZeroValue
          defaultPlaceholderStyle
        />
      );
    case 'boolean':
      return (
        filter.lookup || [
          { name: i18next.t('common.yes'), value: true },
          { name: i18next.t('common.no'), value: false },
        ]
      ).map((item) => (
        <UiCheckbox
          clean
          key={item.name}
          formProps={formProps}
          label={item.name}
          onChange={(changedName) => {
            /** uncheck the opposite value if it is checked. only one value can be checked */
            const foundOnlyName = /\[(.*)]/.exec(changedName);
            if (foundOnlyName) {
              const oppositeName = `${name}[${foundOnlyName[1] === 'true' ? 'false' : 'true'}]`;

              if (formProps.getValues(oppositeName)) {
                formProps.setValue(oppositeName, false);
              }
            }
          }}
          name={`${name}[${item.value ? 'true' : 'false'}]`}
        />
      ));
    case 'intersect':
    case 'lookup':
    case 'lookupText':
      return filter.lookup?.map((item) => (
        <UiCheckbox clean key={item.name} formProps={formProps} label={item.name} name={`${name}[${item.value}_]`} />
      ));
    case 'text':
    case 'anyContains':
      return (
        <UiInput
          material
          defaultPlaceholderStyle
          formProps={formProps}
          name={name}
          placeholder={t('placeholders.text')}
          required
          autoFocus
        />
      );
    case 'phoneNumber':
      return <UiMaskedInput material formProps={formProps} name={name} mask="(999) 999-9999" autoFocus />;
    case 'numeric':
      return (
        <>
          <UiFormatNumberInput
            formProps={formProps}
            name={getInputMinName(name)}
            thousandSeparator=","
            max={9_999_999_999_999}
            min={0}
            placeholder={t('placeholders.numericMin')}
            autoFocus
            allowZeroValue
            material
            defaultPlaceholderStyle
          />
          <UiFormatNumberInput
            formProps={formProps}
            name={getInputMaxName(name)}
            thousandSeparator=","
            max={9_999_999_999_999}
            min={0}
            placeholder={t('placeholders.numericMax')}
            allowZeroValue
            material
            defaultPlaceholderStyle
          />
        </>
      );
    case 'date':
      return (
        <>
          <UiDatePicker
            formProps={formProps}
            name={getInputMinName(name)}
            placeholder={t('placeholders.dateMin')}
            autoFocus
            showClearBtn
            material
          />
          <UiDatePicker
            formProps={formProps}
            name={getInputMaxName(name)}
            placeholder={t('placeholders.dateMax')}
            showClearBtn
            material
          />
        </>
      );
    default:
      return null;
  }
};

export const uiTableGetDefaultValues = (
  values: Record<string, SafeAnyType> | string | number,
  name: string,
  type: UiTableFilterType['type']
): Record<string, SafeAnyType> => {
  if (isNil(values)) {
    return {};
  }

  values = isNumber(values) ? values.toString() : values;

  if (type === 'numeric' || type === 'date') {
    const [min, max] = values as [number | string | undefined, number | string | undefined];

    return {
      // eslint-disable-next-line no-nested-ternary
      [getInputMinName(name)]: type === 'numeric' ? (min ? +min : undefined) : min,
      // eslint-disable-next-line no-nested-ternary
      [getInputMaxName(name)]: type === 'numeric' ? (max ? +max : undefined) : max,
    };
  }

  if (type === 'intersect' || type === 'lookup' || type === 'lookupText') {
    values = values.split(',').reduce((acc: Record<string, boolean>, value: string) => {
      acc[`${value}_`] = true;
      return acc;
    }, {});
  }

  if (type === 'boolean') {
    values = { true: values === 'true', false: values === 'false' };
  }

  return {
    [name]: values,
  };
};

/** other types must submit a form when values change */
const typesWithActionButtons: UiTableFilterType['type'][] = [
  'integer',
  'text',
  'numeric',
  'date',
  'anyContains',
  'phoneNumber',
];

const debounceDelay = 700;

/** end helpers */

type PropsSingle = {
  filter: UiTableFilterType;
  close: () => void;
  formProps: StateFormReturnType;
  onSubmit: (data: BaseTableFilterFiltersType[0]) => void;
  filters: BaseTableFilterFiltersType;

  _closeOnSelect?: boolean; // do not use this
};

type FormValues = Record<string, SafeAnyType>;

/** base (single/object filter) filter */
export const UiTableFormFilter: React.FC<PropsSingle & { innerUseMultiple?: boolean }> = memo(
  ({
    filter,
    close,
    formProps: { getValue, setValue },
    onSubmit: onSubmitForm,
    filters,
    innerUseMultiple = false,
    _closeOnSelect = false,
  }: PropsSingle & { innerUseMultiple?: boolean }) => {
    const tHelper = useTHelper('table.filter');

    const { type, propertyName: name } = filter;

    const typesWithActions = typesWithActionButtons.includes(type);

    const getFilterValue = () => {
      const foundFilter = filters.find((item) => item.PropertyName === name);

      if (foundFilter && (foundFilter.ValueFrom || foundFilter.ValueTo)) {
        return [foundFilter.ValueFrom, foundFilter.ValueTo];
      }

      return foundFilter?.Value || '';
    };

    /** it's very diffucult to move on RHF */
    const localFormProps = useForm<FormValues>({
      mode: 'onChange',
      defaultValues: uiTableGetDefaultValues(getFilterValue(), name, type),
    });

    const {
      getValues: localGetValues,
      formState: { isDirty },
      watch,
    } = localFormProps;

    const onSubmit = useCallback(() => {
      if (typesWithActions || _closeOnSelect) {
        close();
      }

      const filterType = uiTableGetType(type);

      if (type === 'numeric' || type === 'date') {
        // there are 2 values
        const valueFrom = localGetValues(getInputMinName(name));
        const valueTo = localGetValues(getInputMaxName(name));

        setValue(name, [valueFrom, valueTo]);

        onSubmitForm({ PropertyName: name, ValueFrom: valueFrom, ValueTo: valueTo, FilterType: filterType });
        return;
      }

      let value = localGetValues(name);

      if (type === 'intersect' || type === 'lookup') {
        value = Object.entries(value)
          .filter(([, v]) => v)
          .map(([k]) => parseInt(k, 10))
          .join();
      } else if (type === 'lookupText') {
        value = Object.entries(value)
          .filter(([, v]) => v)
          // remove underscore
          .map(([k]) => k.slice(0, -1))
          .join();
      } else if (type === 'integer') {
        value = +value;
      } else if (type === 'boolean') {
        // eslint-disable-next-line prefer-destructuring
        value = Object.entries(value)
          .filter(([, v]) => v)
          .flat()[0];
      }

      if (value !== getValue(name)) {
        setValue(name, value);
        onSubmitForm({ PropertyName: name, Value: value, FilterType: filterType });
      }
    }, [typesWithActions, type, _closeOnSelect, localGetValues, name, getValue, close, setValue, onSubmitForm]);

    const onReset = useCallback(() => {
      if (typesWithActions || _closeOnSelect) {
        close();
      }

      /** reset if the value exists */
      if (!isNil(getValue(name))) {
        onSubmitForm({ PropertyName: name, Value: undefined, FilterType: 0 });
        setValue(name, null);
      }
    }, [typesWithActions, _closeOnSelect, getValue, name, close, onSubmitForm, setValue]);

    const component = getFilter({
      filter,
      name,
      formProps: localFormProps,
      t: tHelper,
    });

    /** functions for submit on values change */
    const debouncedSubmit = useMemo(
      () =>
        debounce((fn: typeof onSubmit) => {
          fn();
        }, debounceDelay),
      []
    );

    const dirtyFieldsDependency = JSON.stringify(watch());

    useEffect(() => {
      if (isDirty && !typesWithActions) {
        if (innerUseMultiple) {
          onSubmit();
        } else {
          debouncedSubmit(onSubmit);
        }
      }
    }, [debouncedSubmit, innerUseMultiple, isDirty, onSubmit, typesWithActions, dirtyFieldsDependency]);
    /** end functions... */

    return component ? (
      <form onSubmit={localFormProps.handleSubmit(onSubmit)} onReset={onReset} autoComplete="off">
        <div className="column-filter custom">
          {component}

          {typesWithActions && (
            <>
              <UiButton type="submit" className="apply-filter-btn" notBtn>
                {tHelper('submit')}
              </UiButton>
              <UiButton type="reset" className="remove-filter-btn" notBtn>
                {tHelper('reset')}
              </UiButton>
            </>
          )}
        </div>
      </form>
    ) : (
      <h3>Oops</h3>
    );
  }
);

type PropsMultiple = Omit<PropsSingle, 'filter' | 'onSubmit'> & {
  filter: Array<UiTableFilterType & { header?: string }>;
  onSubmit: (data: BaseTableFilterFiltersType) => void;

  _closeOnSelect?: boolean; // do not use this
};

/** base (array object filter) filter */
export const UiTableFormFilterMultiple: React.FC<PropsMultiple> = memo<PropsMultiple>(
  ({ filter, close, formProps, onSubmit, filters, _closeOnSelect = false }) => {
    const debouncedSubmit = useMemo(
      () =>
        debounce((fn: () => void) => {
          fn();
        }, debounceDelay),
      []
    );

    const tempArrRef = useRef<BaseTableFilterFiltersType>([]);

    const emptyFn = useCallback(() => null, []);

    const checkSubmit = useCallback(
      (close: () => void) => (data: BaseTableFilterFiltersType[0]) => {
        const index = tempArrRef.current.findIndex(({ PropertyName }) => PropertyName === data.PropertyName);

        if (~index) {
          tempArrRef.current.splice(index, 1, data);
        } else {
          tempArrRef.current.push(data);
        }

        debouncedSubmit(() => {
          onSubmit(tempArrRef.current);

          if (_closeOnSelect) {
            close();
          }

          tempArrRef.current = [];
        });
      },
      [_closeOnSelect, debouncedSubmit, onSubmit]
    );

    return (
      <>
        {filter.map((filt) => (
          <Fragment key={`${filt.type}${filt.propertyName}`}>
            <div className="multiple-filter-title HeaderTextColor">{filt.header}</div>
            <UiTableFormFilter
              filter={filt}
              close={emptyFn}
              formProps={formProps}
              onSubmit={checkSubmit(close)}
              filters={filters}
              innerUseMultiple
            />
          </Fragment>
        ))}
      </>
    );
  }
);
